import { RouletteScene } from '../scenes/RouletteScene';
import { BetManager } from './BetManager';

export interface ChipInfo {
	value: number;
	quantity: number;
	color: string;
	startX?: number;
	startY?: number;
}

export class ChipManager {
	private scene: RouletteScene;
	private placedChipsMap: Map<string, Phaser.GameObjects.Container>;
	private selectedChip: Phaser.GameObjects.Container | null = null;
	private chipData: ChipInfo[];
	private betManager: BetManager;
	private initialX: number;
	private initialY: number;
	private useChipSpinEffect: Boolean = true;

	// 1) Mapeamento de denominações (maior → menor)
	private readonly CHIP_DENOMINATIONS: { value: number; texture: string }[] = [
		{ value: 500, texture: 'chip-yellow' },
		{ value: 250, texture: 'chip-purple' },
		{ value: 100, texture: 'chip-pink' },
		{ value: 50, texture: 'chip-green' },
		{ value: 25, texture: 'chip-gray' },
		{ value: 10, texture: 'chip-blue' },
		{ value: 5, texture: 'chip-black' },
		{ value: 1, texture: 'chip-orange' }
	];

	constructor(
		scene: RouletteScene,
		x: number,
		y: number,
		placedChipsMap: Map<string, Phaser.GameObjects.Container>,
		chipData: ChipInfo[],
		betManager: BetManager,
		useChipSpinEffect: boolean
	) {
		this.scene = scene;
		this.placedChipsMap = placedChipsMap;
		this.chipData = chipData;
		this.initialX = x;
		this.initialY = y;
		this.betManager = betManager;
		this.createChips();
		this.useChipSpinEffect = useChipSpinEffect;
	}

	// Cria as fichas (chips) e configura a interação
	private createChips() {
		this.chipData.forEach((chipInfo, index) => {
			// Posição inicial fora da tela à esquerda para o novo efeito
			const startX = -100; // Ficha começa fora da tela à esquerda
			const startY = this.initialY; // Posição Y inicial

			// Posição final onde a ficha deve ficar
			const finalX = this.initialX + index * 70; // A posição final no eixo X
			const finalY = this.initialY; // A posição final no eixo Y
			const chip = this.scene.add.image(0, 0, `chip-${chipInfo.color}`).setScale(0.8);
			const chipShadow = this.scene.add.image(5, 5, `chip-${chipInfo.color}`).setTint(0x000000).setAlpha(0.3).setScale(0.8);
			chipInfo.startX = finalX;
			chipInfo.startY = finalY;

			const chipText = this.scene.add
				.text(0, 0, chipInfo.value.toString(), {
					fontSize: '16px',
					color: '#FFFFFF',
					fontStyle: 'bold',
					stroke: '#000000',
					strokeThickness: 6
				})
				.setOrigin(0.5);

			const chipData = this.scene.add.container(finalX, finalY, [chipShadow, chip, chipText]);
			chipData.setSize(chip.width, chip.height);
			chipData.setData('value', chipInfo.value);
			chipData.setData('color', chipInfo.color);

			// Armazenar as posições iniciais para uso no evento "Undo"
			//chipData.setData('startX', finalX);
			//chipData.setData('startY', finalY);
			chipData.setData('startX', finalX + 100);
			chipData.setData('startY', 5);

			chipData.setInteractive();
			chipData.on('pointerdown', () => {
				this.handleChipSelection(chipData);
			});
			this.scene.chips.push(chipData);
		});
	}

	// ------
	// 2) Função que, dado um total, retorna array de valores representando o breakdown
	private getChipBreakdown(total: number): number[] {
		const result: number[] = [];
		let remaining = total;

		for (const { value } of this.CHIP_DENOMINATIONS) {
			const count = Math.floor(remaining / value);
			for (let i = 0; i < count; i++) {
				result.push(value);
			}
			remaining -= count * value;
		}

		return result;
	}

	// 3) Função que (re)desenha os filhos do container conforme o breakdown
	public redrawChips(placedChip: Phaser.GameObjects.Container) {
		// pega total armazenado
		const total = placedChip.getData('value') as number;

		// limpa tudo
		placedChip.removeAll(true);

		// obtém breakdown
		const breakdown = this.getChipBreakdown(total);

		// espaçamento entre chips no container
		const spacing = 20;
		const startX = -((breakdown.length - 1) * spacing) / 2;

		breakdown.forEach((chipValue, idx) => {
			const { texture } = this.CHIP_DENOMINATIONS.find(d => d.value === chipValue)!;

			// sombra
			const shadow = this.scene.add.image(3, 3, texture).setScale(0.8).setTint(0x000000).setAlpha(0.3);

			// chip
			const chip = this.scene.add.image(0, 0, texture).setScale(0.8);

			// texto
			const text = this.scene.add
				.text(0, 0, chipValue.toString(), {
					fontSize: '16px',
					color: '#FFFFFF',
					fontStyle: 'bold',
					stroke: '#000000',
					strokeThickness: 6
				})
				.setOrigin(0.5);

			// container interno para alinhar sombra, chip e texto
			const single = this.scene.add.container(startX + idx * spacing, 0, [shadow, chip, text]);

			placedChip.add(single);
		});
	}
	// ------

	// Método para lidar com a seleção de fichas
	private handleChipSelection(chipContainer: Phaser.GameObjects.Container) {
		const currentBalance = this.scene.rouletteBalanceValue;
		if (currentBalance > 0) {
			// Se a ficha tiver quantidade disponível, selecione-a
			if (this.selectedChip) {
				this.selectedChip.setScale(1); // Reseta a escala da ficha anterior
			}
			this.selectedChip = chipContainer;
			this.selectedChip.setScale(1.2); // Aumenta a escala da ficha selecionada
		} else {
			// Se a quantidade for 0, e a ficha estiver selecionada, deselecione-a
			if (this.selectedChip === chipContainer) {
				this.selectedChip.setScale(1); // Reseta a escala
				this.selectedChip = null; // Deseleciona a ficha
				console.log(`Deselecionada ficha com valor ${chipContainer.getData('value')} pois a quantidade é 0.`);
			} else {
				console.log(`No more chips of value ${chipContainer.getData('value')} left!`);
			}
		}
	}

	// Coloca uma ficha no número selecionado com o efeito de giro
	private placeChipOnNumber(numberArea: string, chipValue: number, chipColor: string) {
		let placedChip = this.placedChipsMap.get(numberArea);

		// Snapshot ANTES de qualquer mudança
		const balanceBefore = this.scene.rouletteBalanceValue;
		const betBefore = this.scene.rouletteBetValue;
		const betsBefore = this.betManager.getBetsSnapshot();

		let valuesBefore: number[] = [];
		let valuesAfter: number[] = [];

		if (!placedChip) {
			// Pega a posição inicial da ficha de onde ela está
			const startX = this.selectedChip?.x || this.initialX;
			const startY = this.selectedChip?.y || this.initialY;

			// Cria um container para a ficha na posição inicial
			placedChip = this.scene.add.container(startX, startY);

			const newChip = this.scene.add.image(0, 0, `chip-${chipColor}`).setScale(0.8);
			const newChipShadow = this.scene.add.image(3, 3, `chip-${chipColor}`).setScale(0.8).setTint(0x000000).setAlpha(0.3);
			const newChipText = this.scene.add
				.text(0, 0, chipValue.toString(), {
					fontSize: '16px',
					color: '#FFFFFF',
					fontStyle: 'bold',
					stroke: '#000000',
					strokeThickness: 6
				})
				.setOrigin(0.5);

			placedChip.add([newChipShadow, newChip, newChipText]);
			placedChip.setData('value', chipValue);
			placedChip.setData('quantity', 1);
			this.placedChipsMap.set(numberArea, placedChip);

			// Atualiza composição
			valuesBefore = [];
			valuesAfter = [chipValue];

			// Aposta
			this.betManager.addBet(numberArea, chipValue);

			// Se o efeito de giro estiver habilitado, aplica a animação
			if (this.useChipSpinEffect) {
				this.animateChipPlacement(placedChip, numberArea);
			} else {
				// Move diretamente para a área sem animação
				//placedChip.setPosition(numberArea.getCenter().x, numberArea.getCenter().y);
			}
		} else {
			// Se já existe uma ficha, atualiza o valor e quantidade
			const currentTotalValue = placedChip.getData('value');
			const currentQuantityChips = placedChip.getData('quantity');
			const newTotalValue = currentTotalValue + chipValue;

			valuesBefore = this.getChipBreakdown(currentTotalValue);
			valuesAfter = this.getChipBreakdown(newTotalValue);

			placedChip.setData('value', newTotalValue);
			placedChip.setData('quantity', currentQuantityChips + 1);

			this.betManager.addBet(numberArea, chipValue);
		}

		// Snapshot DEPOIS da modificação
		const balanceAfter = this.scene.rouletteBalanceValue;
		const betAfter = this.scene.rouletteBetValue;
		const betsAfter = this.betManager.getBetsSnapshot();

		// Salva no histórico
		this.scene.history.push({
			numberArea,
			valuesBefore,
			valuesAfter,
			chip: placedChip,
			coinValue: chipValue,
			balanceBefore,
			balanceAfter,
			betBefore,
			betAfter,
			betsSnapshotBefore: betsBefore,
			betsSnapshotAfter: betsAfter
		});

		// redesenha todos os chips no container
		this.redrawChips(placedChip);
	}

	// Anima o chip girando e se movendo diagonalmente até a posição final
	private animateChipPlacement(placedChip: Phaser.GameObjects.Container, numberArea: string) {
		let targetX = 0;
		let targetY = 0;

		const { x, y }: any = this.getPositionByName(numberArea);

		targetX = x;
		targetY = y;

		// Tween para movimento e rotação em diagonal
		this.scene.tweens.add({
			targets: placedChip,
			x: targetX,
			y: targetY,
			rotation: Phaser.Math.DegToRad(360),
			duration: 1000,
			ease: 'Power2',
			onComplete: () => {
				placedChip.setRotation(0);
			}
		});
	}

	// Anima o chip movendo da esquerda para a posição final, uma por vez
	private animateLeftToRightPlacement(placedChip: Phaser.GameObjects.Container, finalX: number, finalY: number, index: number) {
		this.scene.tweens.add({
			targets: placedChip,
			x: finalX,
			y: finalY,
			rotation: Phaser.Math.DegToRad(360),
			duration: 1000,
			ease: 'Power2',
			onComplete: () => {
				placedChip.setRotation(0);

				const quantityText = placedChip.getData('quantityText') as Phaser.GameObjects.Text;
				this.scene.tweens.add({
					targets: quantityText,
					alpha: { from: 0, to: 1 },
					duration: 500,
					ease: 'Power2'
				});

				if (index + 1 < this.scene.chips.length) {
					const nextChip = this.scene.chips[index + 1];
					this.animateLeftToRightPlacement(nextChip, this.initialX + (index + 1) * 70, this.initialY, index + 1);
				}
			}
		});
	}

	// Pega o ID do scene
	private getRouletteID() {
		return this.scene.rouletteID;
	}

	private findInContainerByName(container: Phaser.GameObjects.Container, name: string): Phaser.GameObjects.GameObject | null {
		for (const child of container.list) {
			if (child.name === name) return child;

			if (child instanceof Phaser.GameObjects.Container) {
				const found = this.findInContainerByName(child, name);
				if (found) return found;
			}
		}
		return null;
	}

	// PUBLIC ------------
	public getChipSelected() {
		return parseInt(this.selectedChip?.getData('value'), 10);
	}

	// Lida com o clique nas áreas dos números
	public handleNumberAreaClick(numberArea: string) {
		const selectedChip = this.selectedChip;
		if (selectedChip) {
			const currentBalance = this.scene.rouletteBalanceValue;
			const selectedChipValue = selectedChip.getData('value');
			const selectedChipColor = selectedChip.getData('color');

			if (currentBalance > 0) {
				this.placeChipOnNumber(numberArea, selectedChipValue, selectedChipColor);
				this.scene.trackMove(selectedChip, numberArea);
			}
		}
	}

	// Adiciona um getter para acessar chipData
	public getChipData(): ChipInfo[] {
		return this.chipData;
	}

	public getPositionByName(name: string): { x: number; y: number } | null {
		const container = this.scene.children.getByName('areaNumbers') as Phaser.GameObjects.Container;
		if (!container) return null;

		const obj = this.findInContainerByName(container, name);
		if (!obj) return null;

		const checkCenter = obj.getData?.('center');

		// 1. Tenta usar bounds
		if ('getBounds' in obj && typeof (obj as any).getBounds === 'function') {
			const bounds = (obj as any).getBounds();
			if (bounds.width > 0 && bounds.height > 0) {
				return { x: bounds.centerX, y: bounds.centerY };
			}
		}

		// 2. Fallback usando posição absoluta
		if ('x' in obj && 'y' in obj && typeof (obj as any).x === 'number' && typeof (obj as any).y === 'number' && !checkCenter) {
			const casted = obj as { x: number; y: number };
			return { x: casted.x, y: casted.y };
		}

		// 3. Se não tiver x/y, tenta buscar no data
		if (typeof obj.getData === 'function') {
			const center = obj.getData('center');
			if (center && typeof center.x === 'number' && typeof center.y === 'number') {
				return { x: center.x, y: center.y };
			}
		}

		// 4. Nada encontrado
		return null;
	}

	public unSelectAllChips() {
		const currentBalance = this.scene.rouletteBalanceValue;
		this.scene.chips.forEach(chipContainer => {
			chipContainer.setScale(1);
			this.selectedChip = null;
		});
	}

	public updateAvailableChips() {
		const currentBalance = this.scene.rouletteBalanceValue;
		this.scene.chips.forEach(chipContainer => {
			const chipValue = chipContainer.getData('value') as number;

			if (chipValue > currentBalance) {
				chipContainer.setAlpha(0.3);
				chipContainer.disableInteractive();

				// Se estiver selecionada, desmarcar
				if (this.selectedChip === chipContainer) {
					chipContainer.setScale(1); // reset escala
					this.selectedChip = null;
				}
			} else {
				chipContainer.setAlpha(1);
				chipContainer.setInteractive();
			}
		});
	}
}
